import { type ReactNode, useId } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import type { ServedQuestion } from "@patent-studio/api-types";
import { toast } from "sonner";
import { ActionBar } from "@/components/patterns/ActionBar";
import { EmptyState } from "@/components/ui/EmptyState";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { Note } from "@/components/ui/Note";
import { Panel } from "@/components/ui/Panel";
import { StatusPill } from "@/components/ui/StatusPill";
import { useQuestionWrites, useQuestions } from "@/hooks/use-questions";
import { refusalDetail } from "@/services/api-client";
import { StagePage } from "@/v2/layout/StagePage";
import { findStage, stagePath, subStepsOf } from "@/v2/stage-vocabulary";
import { askedAt, unresolved } from "./asked-at";
import { WhyNote } from "./QuestionCard";
import { QuestionWords } from "./QuestionWords";
import { NOT_SAID, ReplyControls } from "./ReplyControls";

type Writes = ReturnType<typeof useQuestionWrites>;

const SAVED = "Answer saved. Moved to Resolved Questions.";

/** Where the attorney came from, and what that page is called, as the bar names it: Intake when nowhere in the draft. */
function cameFrom(
  taskId: string,
  from: unknown,
): { path: string; label: string } {
  const path = typeof from === "string" ? from : "";
  const [, drafts, task, stageId, stepId] = path.split("/");
  const stage =
    drafts === "drafts" && task === taskId ? findStage(stageId) : undefined;
  if (stage === undefined) {
    return { path: stagePath(taskId, "intake"), label: "Intake" };
  }
  // A Thesis page goes by its own name, as the design's bar does: Invention, Novelty.
  const step =
    stage.id === "thesis"
      ? subStepsOf(stage.id)?.steps.find((s) => s.id === stepId)
      : undefined;
  return { path, label: step?.label ?? stage.label };
}

/** A list's head: its name, and what it holds beside it. */
function ListHead({
  id,
  name,
  count,
}: {
  id: string;
  name: string;
  count: string;
}) {
  return (
    <div className="mb-3 mt-7 flex items-baseline gap-2.5">
      <MicroLabel as="h2" id={id} size="sm">
        {name}
      </MicroLabel>
      {count !== "" && (
        <span className="text-[11.5px] text-muted-foreground">{count}</span>
      )}
    </div>
  );
}

/** One open question: where it was asked, the question and why, and the reply that answers it here. */
function OpenCard({
  taskId,
  question,
  writes,
}: {
  taskId: string;
  question: ServedQuestion;
  writes: Writes;
}) {
  const id = useId();
  return (
    <Panel role="group" aria-labelledby={id} pad="snug">
      <MicroLabel as="p" size="sm" className="mb-1.5 text-brand-mid">
        {askedAt(question)}
      </MicroLabel>
      <p id={id} className="text-sm font-semibold text-pretty">
        <QuestionWords taskId={taskId} text={question.question} />
      </p>
      <WhyNote question={question} />
      <ReplyControls
        question={question}
        writes={writes}
        canSkip={false}
        onDone={() => {
          writes.submit.mutate([question.question_id], {
            onSuccess: () => toast.success(SAVED),
          });
        }}
      />
    </Panel>
  );
}

/** The answer given, as the row shows it: the options chosen, then the comment. */
function Answer({ question }: { question: ServedQuestion }) {
  const given = question.answer ?? question.draft;
  const choices = given?.not_said
    ? NOT_SAID
    : (given?.choices ?? []).join(", ");
  const said = [choices, given?.text ?? ""].filter((part) => part !== "");
  return (
    <p className="mt-1.5 text-detail">
      <MicroLabel as="span" size="sm" className="mr-1.5 text-subtle-foreground">
        Answer
      </MicroLabel>
      <span className="font-medium italic">{said.join(" · ")}</span>
    </p>
  );
}

/** One resolved question: answered, with its answer, or closed by Praxis, with why. */
function ResolvedRow({
  taskId,
  question,
}: {
  taskId: string;
  question: ServedQuestion;
}) {
  const closed = question.status === "closed_by_praxis";
  return (
    <li>
      <Panel pad="snug" className="flex items-start gap-3.5">
        <div className="min-w-0 flex-1">
          <MicroLabel as="p" size="sm" className="mb-1.5 text-brand-mid">
            {askedAt(question)}
          </MicroLabel>
          <p className="text-[13px] font-semibold text-pretty">
            <QuestionWords taskId={taskId} text={question.question} />
          </p>
          {closed ? (
            <p className="mt-1.5 text-xs text-muted-foreground text-pretty">
              {question.closure_reason}
            </p>
          ) : (
            <Answer question={question} />
          )}
        </div>
        <StatusPill tone={closed ? "neutral" : "success"} plain>
          {closed ? "No longer needed" : "Answered"}
        </StatusPill>
      </Panel>
    </li>
  );
}

/** A list, or the line that says it is empty. */
function Listed({
  name,
  count,
  shown,
  empty,
  children,
}: {
  name: string;
  /** What the list holds, said beside its name; nothing when it holds nothing. */
  count: string;
  shown: boolean;
  empty: string;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <section aria-labelledby={id}>
      <ListHead id={id} name={name} count={count} />
      {shown ? (
        children
      ) : (
        <EmptyState className="px-4 py-3.5 text-[13px] text-muted-foreground">
          {empty}
        </EmptyState>
      )}
    </section>
  );
}

/** When the question was resolved, as a time to order by; never resolved reads as the earliest. */
function resolvedAt(question: ServedQuestion): number {
  return question.resolved_at ? Date.parse(question.resolved_at) : 0;
}

/** Both lists, once the questions are read: the open ones answerable, the resolved ones newest first. */
function Lists({
  taskId,
  questions,
  writes,
}: {
  taskId: string;
  questions: readonly ServedQuestion[];
  writes: Writes;
}) {
  const open = questions.filter(unresolved);
  const resolved = questions
    .filter((q) => !unresolved(q))
    .sort((a, b) => resolvedAt(b) - resolvedAt(a));
  return (
    <>
      <Listed
        name="Open"
        count={open.length > 0 ? `${String(open.length)} to answer` : ""}
        shown={open.length > 0}
        empty="No open questions."
      >
        <div className="flex flex-col gap-3">
          {open.map((question) => (
            <OpenCard
              key={question.question_id}
              taskId={taskId}
              question={question}
              writes={writes}
            />
          ))}
        </div>
      </Listed>
      {writes.submit.isError && (
        <Note tone="destructive" role="alert" className="mt-2">
          {refusalDetail(
            writes.submit.error,
            "Your answer could not be saved. Try again.",
          )}
        </Note>
      )}
      <Listed
        name="Resolved Questions"
        count={resolved.length > 0 ? String(resolved.length) : ""}
        shown={resolved.length > 0}
        empty="Questions you answer, or that are answered later in drafting, appear here."
      >
        <ul className="flex flex-col gap-2">
          {resolved.map((question) => (
            <ResolvedRow
              key={question.question_id}
              taskId={taskId}
              question={question}
            />
          ))}
        </ul>
      </Listed>
    </>
  );
}

/**
 * Open Questions: the questions still to answer, each answerable here, then
 * the ones resolved, and the bar back to the page the attorney left. A reply
 * answers the question at once and moves it down the page.
 */
export function OpenQuestionsPage() {
  const { taskId = "" } = useParams<{ taskId: string }>();
  const from = (useLocation().state as { from?: unknown } | null)?.from;
  const navigate = useNavigate();
  const store = useQuestions(taskId);
  const writes = useQuestionWrites(taskId);
  const back = cameFrom(taskId, from);
  return (
    <StagePage
      testId="open-questions-page"
      taskId={taskId}
      title="Open Questions"
      lede="Questions you chose to answer later. Answering them before you generate helps Praxis produce an accurate draft."
    >
      {store.data !== undefined && (
        <Lists
          taskId={taskId}
          questions={store.data.questions}
          writes={writes}
        />
      )}
      <ActionBar
        testId="open-questions-back"
        state="plain"
        sticky
        heading={`Back to ${back.label}`}
        detail="Return to the screen you were on before opening Open Questions."
        actionLabel={`Back to ${back.label}`}
        onAction={() => {
          void navigate(back.path);
        }}
      />
    </StagePage>
  );
}
